/**
 * Task 8 (media images): turns a stored media id into a same-origin blob URL
 * an `<img>` can point `src` at, via the staff-session-gated `GET
 * /ops/media/:id` (`ops.mediaBlob`, see lib/ops.ts). `Message.media`/`MediaRef`
 * never carries a URL of its own (packages/shared/src/media.ts's header: "an
 * image is an id and a mime, and where its bytes are served from is the
 * route's business") — this hook is that route's business, on the console
 * side.
 *
 * REVOKES ON UNMOUNT AND ON EVERY ID CHANGE. `URL.createObjectURL` pins the
 * blob in memory for the life of the document unless explicitly revoked — a
 * transcript with dozens of thumbnails that never revoked would leak one
 * blob per image for as long as the tab stayed open. The effect's cleanup
 * revokes the URL it created, not whatever `url` state happens to hold at
 * cleanup time, so a fast id change can never revoke a URL a still-mounted
 * caller is showing.
 *
 * jsdom has no `URL.createObjectURL` — tests stub it globally the same way
 * `test/push.test.ts` stubs `Notification`/`PushManager` (assign directly to
 * the global, restore in `afterEach`).
 */
import { useEffect, useState } from 'react'
import { ops } from './ops'

export interface UseMediaBlobResult {
  /** A blob: URL once the fetch resolves; null while loading or on failure. */
  url: string | null
  /** True once the fetch has failed (a 404, a network error, anything non-2xx). */
  failed: boolean
}

export function useMediaBlob(id: string | null | undefined): UseMediaBlobResult {
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setUrl(null)
    setFailed(false)

    if (!id) return

    let cancelled = false
    let createdUrl: string | null = null

    ops
      .mediaBlob(id)
      .then((blob) => {
        if (cancelled) return
        createdUrl = URL.createObjectURL(blob)
        setUrl(createdUrl)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
      if (createdUrl) URL.revokeObjectURL(createdUrl)
    }
  }, [id])

  return { url, failed }
}
